/**
 * @fileoverview Hook for profile data management and updates
 * 
 * @purpose Provides a React hook for fetching, updating, and managing user profile data.
 *          Handles profile fetching, updates, and avatar uploads/deletions.
 * 
 * @usage Import in client components:
 *        import { useProfileUpdates } from '@korvo/core/hooks/useProfileUpdates'
 * 
 * @ownership Core infrastructure - profile hooks
 */

'use client'

import { useCallback, useEffect, useState } from 'react';

import { useSession } from '../../lib/better-auth/client';
import { ERR_CHAT_500 } from '../../lib/error-codes';
import { KorvoError } from '../../lib/korvo-error';
import { type NormalizedError, normalizeError } from '../../lib/normalize-error';

type ProfileData = {
    id: string;
    name: string;
    email: string;
    bio: string;
    website: string;
    timezone: string;
    language: string;
    displayName: string;
    pronouns: string;
    interests: string[];
    emailNotifications: boolean;
    pushNotifications: boolean;
    marketingEmails: boolean;
    profileVisibility: 'PUBLIC' | 'PRIVATE' | 'FRIENDS';
    profileCompleted: boolean;
    avatar?: string;
};

type ProfileResponse = ProfileData;
type ProfileUpdateResponse = {
    user: ProfileData;
};
type AvatarResponse = {
    avatar: string;
};
type ApiErrorResponse = {
    error?: string;
};

export function useProfileUpdates() {
    const { data: session } = useSession();
    const [profileData, setProfileData] = useState<ProfileData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const fetchProfile = useCallback(async () => {
        if (!session?.user?.id)
            return;
        try {
            setIsLoading(true);
            setError(null);
            const response = await fetch('/api/profile');
            if (response.ok) {
                const data = (await response.json()) as ProfileResponse;
                setProfileData(data);
            }
            else {
                throw new KorvoError(ERR_CHAT_500, 1, { message: 'Failed to fetch profile', status: response.status });
            }
        }
        catch (err: unknown) {
            const errNormalized: NormalizedError = normalizeError(err);
            setError(errNormalized.message);
        }
        finally {
            setIsLoading(false);
        }
    }, [session?.user?.id]);
    
    const persistProfileChanges = useCallback(async (updates: Partial<ProfileData>) => {
        if (!session?.user?.id)
            return;
        try {
            setError(null);
            const response = await fetch('/api/profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updates),
            });
            if (response.ok) {
                const data = (await response.json()) as ProfileUpdateResponse;
                setProfileData(prev => prev ? { ...prev, ...data.user } : data.user);
                return { success: true, data: data.user };
            }
            else {
                const errorData = (await response.json()) as ApiErrorResponse;
                throw new KorvoError(ERR_CHAT_500, 2, {
                  message: errorData.error || 'Failed to update profile',
                  status: response.status,
                });
            }
        }
        catch (err: unknown) {
            const errNormalized: NormalizedError = normalizeError(err);
            const errorMessage = errNormalized.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [session?.user?.id]);
    
    const uploadAvatar = useCallback(async (file: File) => {
        if (!session?.user?.id)
            return;
        try {
            setError(null);
            const formData = new FormData();
            formData.append('avatar', file);
            const response = await fetch('/api/profile/avatar', {
                method: 'POST',
                body: formData,
            });
            if (response.ok) {
                const data = (await response.json()) as AvatarResponse;
                setProfileData(prev => prev ? { ...prev, avatar: data.avatar } : null);
                return { success: true, avatar: data.avatar };
            }
            else {
                const errorData = (await response.json()) as ApiErrorResponse;
                throw new KorvoError(ERR_CHAT_500, 3, {
                  message: errorData.error || 'Failed to upload avatar',
                  status: response.status,
                });
            }
        }
        catch (err: unknown) {
            const errNormalized: NormalizedError = normalizeError(err);
            const errorMessage = errNormalized.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [session?.user?.id]);
    
    const deleteAvatar = useCallback(async () => {
        if (!session?.user?.id)
            return;
        try {
            setError(null);
            const response = await fetch('/api/profile/avatar', {
                method: 'DELETE',
            });
            if (response.ok) {
                setProfileData(prev => prev ? { ...prev, avatar: undefined } : null);
                return { success: true };
            }
            else {
                throw new KorvoError(ERR_CHAT_500, 4, {
                  message: 'Failed to remove avatar',
                  status: response.status,
                });
            }
        }
        catch (err: unknown) {
            const errNormalized: NormalizedError = normalizeError(err);
            const errorMessage = errNormalized.message;
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    }, [session?.user?.id]);
    
    useEffect(() => {
        void fetchProfile();
    }, [fetchProfile]);
    
    return {
        profileData,
        isLoading,
        error,
        persistProfileChanges,
        uploadAvatar,
        deleteAvatar,
        refetch: fetchProfile
    };
}
